import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ToggleOn as FlagIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { LoadingScreen } from '../../components/common/LoadingScreen';

interface FeatureFlagDetail {
  id: number;
  name: string;
  description: string | null;
  enabled: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  organization_count: number;
  user_count: number;
}

interface OrgGrant {
  organization_id: number;
  feature_flag_id: number;
  granted_by: string | null;
  granted_at: string;
  organization_name: string;
}

interface UserGrant {
  user_uuid: string;
  feature_flag_id: number;
  granted_by: string | null;
  granted_at: string;
  user_name: string;
  user_email: string;
}

interface GrantsResponse {
  org_grants: OrgGrant[];
  user_grants: UserGrant[];
}

interface OrgOption {
  id: number;
  name: string;
}

interface UserOption {
  uuid: string;
  name: string;
  email: string;
}

export default function FeatureFlagDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [flag, setFlag] = useState<FeatureFlagDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Org grants
  const [orgGrants, setOrgGrants] = useState<OrgGrant[]>([]);

  // User grants
  const [userGrants, setUserGrants] = useState<UserGrant[]>([]);

  // Grants error
  const [grantsError, setGrantsError] = useState<string | null>(null);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editEnabled, setEditEnabled] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Grant org dialog
  const [grantOrgDialogOpen, setGrantOrgDialogOpen] = useState(false);
  const [orgOptions, setOrgOptions] = useState<OrgOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrgOption | null>(null);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [grantOrgLoading, setGrantOrgLoading] = useState(false);
  const [grantOrgError, setGrantOrgError] = useState<string | null>(null);

  // Grant user dialog
  const [grantUserDialogOpen, setGrantUserDialogOpen] = useState(false);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [grantUserLoading, setGrantUserLoading] = useState(false);
  const [grantUserError, setGrantUserError] = useState<string | null>(null);

  // Revoke confirmation
  const [revokeOrgDialogOpen, setRevokeOrgDialogOpen] = useState(false);
  const [selectedOrgGrant, setSelectedOrgGrant] = useState<OrgGrant | null>(null);
  const [revokeUserDialogOpen, setRevokeUserDialogOpen] = useState(false);
  const [selectedUserGrant, setSelectedUserGrant] = useState<UserGrant | null>(null);

  useEffect(() => {
    loadFlag();
  }, [id]);

  const loadFlag = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<FeatureFlagDetail>(`/admin/feature-flags/${id}`);
      setFlag(data);
      loadGrants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feature flag');
    } finally {
      setLoading(false);
    }
  };

  const loadGrants = async () => {
    try {
      setGrantsError(null);
      const data = await apiClient.get<GrantsResponse>(`/admin/feature-flags/${id}/grants`);
      setOrgGrants(data.org_grants);
      setUserGrants(data.user_grants);
    } catch (err) {
      setGrantsError(err instanceof Error ? err.message : 'Failed to load grants');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // --- Edit flag ---

  const openEditDialog = () => {
    if (!flag) return;
    setEditName(flag.name);
    setEditDescription(flag.description || '');
    setEditEnabled(flag.enabled);
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleEdit = async () => {
    try {
      setEditLoading(true);
      setEditError(null);
      await apiClient.put(`/admin/feature-flags/${id}`, {
        name: editName,
        description: editDescription || null,
        enabled: editEnabled,
      });
      setEditDialogOpen(false);
      await loadFlag();
    } catch (err: unknown) {
      setEditError(extractErrorMessage(err, 'Failed to update feature flag'));
    } finally {
      setEditLoading(false);
    }
  };

  // --- Grant to org ---

  const openGrantOrgDialog = async () => {
    setGrantOrgDialogOpen(true);
    setSelectedOrg(null);
    setGrantOrgError(null);

    try {
      setLoadingOrgs(true);
      const allOrgs = await apiClient.get<OrgOption[]>('/admin/organizations');
      const grantedOrgIds = new Set(orgGrants.map((g) => g.organization_id));
      const available = allOrgs.filter((o: OrgOption) => !grantedOrgIds.has(o.id));
      setOrgOptions(available);
    } catch (err) {
      setGrantOrgError(err instanceof Error ? err.message : 'Failed to load organisations');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleGrantOrg = async () => {
    if (!selectedOrg) return;

    try {
      setGrantOrgLoading(true);
      setGrantOrgError(null);
      await apiClient.post(`/admin/organizations/${selectedOrg.id}/feature-flags`, {
        feature_flag_id: Number(id),
      });
      setGrantOrgDialogOpen(false);
      await loadFlag();
    } catch (err: unknown) {
      setGrantOrgError(extractErrorMessage(err, 'Failed to grant feature flag'));
    } finally {
      setGrantOrgLoading(false);
    }
  };

  // --- Grant to user ---

  const openGrantUserDialog = async () => {
    setGrantUserDialogOpen(true);
    setSelectedUser(null);
    setGrantUserError(null);

    try {
      setLoadingUsers(true);
      const allUsers = await apiClient.get<UserOption[]>('/admin/users');
      const grantedUuids = new Set(userGrants.map((g) => g.user_uuid));
      const available = allUsers.filter((u: UserOption) => !grantedUuids.has(u.uuid));
      setUserOptions(available);
    } catch (err) {
      setGrantUserError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleGrantUser = async () => {
    if (!selectedUser) return;

    try {
      setGrantUserLoading(true);
      setGrantUserError(null);
      await apiClient.post(`/admin/users/${selectedUser.uuid}/feature-flags`, {
        feature_flag_id: Number(id),
      });
      setGrantUserDialogOpen(false);
      await loadFlag();
    } catch (err: unknown) {
      setGrantUserError(extractErrorMessage(err, 'Failed to grant feature flag'));
    } finally {
      setGrantUserLoading(false);
    }
  };

  // --- Revoke ---

  const handleRevokeOrg = (grant: OrgGrant) => {
    setSelectedOrgGrant(grant);
    setRevokeOrgDialogOpen(true);
  };

  const handleConfirmRevokeOrg = async () => {
    if (!selectedOrgGrant) return;

    try {
      await apiClient.delete(
        `/admin/organizations/${selectedOrgGrant.organization_id}/feature-flags/${selectedOrgGrant.feature_flag_id}`
      );
      setRevokeOrgDialogOpen(false);
      setSelectedOrgGrant(null);
      await loadFlag();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke feature flag');
    }
  };

  const handleRevokeUser = (grant: UserGrant) => {
    setSelectedUserGrant(grant);
    setRevokeUserDialogOpen(true);
  };

  const handleConfirmRevokeUser = async () => {
    if (!selectedUserGrant) return;

    try {
      await apiClient.delete(
        `/admin/users/${selectedUserGrant.user_uuid}/feature-flags/${selectedUserGrant.feature_flag_id}`
      );
      setRevokeUserDialogOpen(false);
      setSelectedUserGrant(null);
      await loadFlag();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke feature flag');
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading feature flag..." />;
  }

  if (error || !flag) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">
            {error || 'Feature flag not found'}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/feature-flags')}
            sx={{ mt: 2 }}
          >
            Back to Feature Flags
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/feature-flags')}
            sx={{ mb: 2 }}
          >
            Back to Feature Flags
          </Button>
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              <FlagIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'primary.main' }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {flag.name}
                  </Typography>
                  <Chip
                    label={flag.enabled ? 'Enabled' : 'Disabled'}
                    color={flag.enabled ? 'success' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                </Box>
                {flag.description && (
                  <Typography variant="body2" color="text.secondary">
                    {flag.description}
                  </Typography>
                )}
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={openEditDialog}
              size="small"
            >
              Edit
            </Button>
          </Box>
        </Box>

        {/* Grants error */}
        {grantsError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setGrantsError(null)}>
            {grantsError}
          </Alert>
        )}

        {/* Organisations Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Organisations ({orgGrants.length})
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openGrantOrgDialog}>
              Grant to Organisation
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Date Granted</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgGrants.map((grant) => (
                  <TableRow key={grant.organization_id}>
                    <TableCell>{grant.organization_name}</TableCell>
                    <TableCell>{formatDate(grant.granted_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleRevokeOrg(grant)}
                        title="Revoke access"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {orgGrants.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No organisations have this flag
              </Typography>
            </Box>
          )}
        </Box>

        {/* Users Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Users ({userGrants.length})
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openGrantUserDialog}>
              Grant to User
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Date Granted</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userGrants.map((grant) => (
                  <TableRow key={grant.user_uuid}>
                    <TableCell>{grant.user_name}</TableCell>
                    <TableCell>{grant.user_email}</TableCell>
                    <TableCell>{formatDate(grant.granted_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleRevokeUser(grant)}
                        title="Revoke access"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {userGrants.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No users have this flag
              </Typography>
            </Box>
          )}
        </Box>

        {/* Edit Flag Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Feature Flag</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {editError && (
                <Alert severity="error" onClose={() => setEditError(null)}>
                  {editError}
                </Alert>
              )}
              <TextField
                label="Flag Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editEnabled}
                    onChange={(e) => setEditEnabled(e.target.checked)}
                  />
                }
                label="Enabled"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              variant="contained"
              disabled={editLoading || !editName.trim()}
            >
              {editLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Grant to Organisation Dialog */}
        <Dialog open={grantOrgDialogOpen} onClose={() => setGrantOrgDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Grant Flag to Organisation</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {grantOrgError && (
                <Alert severity="error" onClose={() => setGrantOrgError(null)}>
                  {grantOrgError}
                </Alert>
              )}
              {loadingOrgs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Autocomplete
                  options={orgOptions}
                  getOptionLabel={(option) => option.name}
                  value={selectedOrg}
                  onChange={(_, newValue) => setSelectedOrg(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Organisation" placeholder="Search organisations..." />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGrantOrgDialogOpen(false)} disabled={grantOrgLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleGrantOrg}
              variant="contained"
              disabled={grantOrgLoading || !selectedOrg}
            >
              {grantOrgLoading ? 'Granting...' : 'Grant'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Grant to User Dialog */}
        <Dialog open={grantUserDialogOpen} onClose={() => setGrantUserDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Grant Flag to User</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {grantUserError && (
                <Alert severity="error" onClose={() => setGrantUserError(null)}>
                  {grantUserError}
                </Alert>
              )}
              {loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Autocomplete
                  options={userOptions}
                  getOptionLabel={(option) => `${option.name} (${option.email})`}
                  value={selectedUser}
                  onChange={(_, newValue) => setSelectedUser(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Select User" placeholder="Search users..." />
                  )}
                  isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGrantUserDialogOpen(false)} disabled={grantUserLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleGrantUser}
              variant="contained"
              disabled={grantUserLoading || !selectedUser}
            >
              {grantUserLoading ? 'Granting...' : 'Grant'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Revoke Org Confirmation */}
        <ConfirmDialog
          open={revokeOrgDialogOpen}
          title="Revoke Feature Flag"
          message={`Are you sure you want to revoke this flag from "${selectedOrgGrant?.organization_name || 'this organisation'}"?`}
          confirmText="Revoke"
          confirmColor="error"
          onConfirm={handleConfirmRevokeOrg}
          onCancel={() => {
            setRevokeOrgDialogOpen(false);
            setSelectedOrgGrant(null);
          }}
        />

        {/* Revoke User Confirmation */}
        <ConfirmDialog
          open={revokeUserDialogOpen}
          title="Revoke Feature Flag"
          message={`Are you sure you want to revoke this flag from "${selectedUserGrant?.user_name || 'this user'}"?`}
          confirmText="Revoke"
          confirmColor="error"
          onConfirm={handleConfirmRevokeUser}
          onCancel={() => {
            setRevokeUserDialogOpen(false);
            setSelectedUserGrant(null);
          }}
        />
      </Box>
    </Container>
  );
}
